import {
  useNavigationProvider,
  getNavigationShortcuts
} from "./navigation-provider"
import {
  useSubscriberSearchProvider,
  getSubscriberSearchShortcuts
} from "./subscriber-search-provider"
import {
  useTagSearchProvider,
  getTagSearchShortcuts
} from "./tags-search-provider"
import {
  useSegmentSearchProvider,
  getSegmentSearchShortcuts
} from "./segments-search-provider"
import {
  useBroadcastSearchProvider,
  getBroadcastSearchShortcuts
} from "./broadcasts-search-provider"
import {
  useSequenceSearchProvider,
  getSequenceSearchShortcuts
} from "./sequences-search-provider"
import {
  useAutomationSearchProvider,
  getAutomationSearchShortcuts
} from "./automations-search-provider"

// The provider registry. Order here is the display order of contributed
// commands. It MUST stay static (module-level) — the palette calls each entry
// as a hook, so a changing length/order would break React's rules of hooks.
//
// Add a capability by appending its provider hook here.
const PROVIDERS = [
  useSubscriberSearchProvider,
  useTagSearchProvider,
  useSegmentSearchProvider,
  useBroadcastSearchProvider,
  useSequenceSearchProvider,
  useAutomationSearchProvider,
  useNavigationProvider
]

/**
 * Runs every registered provider and merges their output.
 *
 * @param {{ query: string, activePageId: string|null, context: import("../types").CommandContext }} args
 * @returns {{ commands: import("../types").Command[], isLoading: boolean }}
 */
export function useCommandProviders(args) {
  // Safe despite the lint: PROVIDERS is a module-level constant, so the number
  // and order of hook calls is identical on every render.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const results = PROVIDERS.map(useProvider => useProvider(args))
  return {
    commands: results.flatMap(result => result.commands),
    isLoading: results.some(result => result.isLoading)
  }
}

// Pure (non-hook) sources of global keyboard chords, one per capability that
// has them. Add a provider's chords by appending its getter here.
const SHORTCUT_SOURCES = [
  getNavigationShortcuts,
  getSubscriberSearchShortcuts,
  getTagSearchShortcuts,
  getSegmentSearchShortcuts,
  getBroadcastSearchShortcuts,
  getSequenceSearchShortcuts,
  getAutomationSearchShortcuts
]

/**
 * All visible keyboard chords across providers. Pure, so it runs outside React
 * (the hotkeys handler reads it without subscribing to provider state).
 *
 * @param {import("../types").CommandContext} context
 * @returns {import("../types").Shortcut[]}
 */
export function getShortcuts(context) {
  return SHORTCUT_SOURCES.flatMap(source => source(context))
}
